const http = require('http');
const fs = require('fs');
const path = require('path');
const { getRequest, releaseRequest } = require('./lib/request');
const { getResponse, releaseResponse } = require('./lib/response');

const MIME_TYPES = {
    '.html': 'text/html',
    '.css': 'text/css',
    '.js': 'application/javascript',
    '.json': 'application/json',
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.svg': 'image/svg+xml',
    '.ico': 'image/x-icon'
};

function sanitizePayload(obj) {
    if (obj === null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(sanitizePayload);
    const proto = Object.getPrototypeOf(obj);
    if (proto !== null && proto !== Object.prototype) return obj;
    const sanitized = {};
    for (const key in obj) {
        if (key === '__proto__' || key === 'constructor') continue;
        sanitized[key] = sanitizePayload(obj[key]);
    }
    return sanitized;
}

class RequestContext {
    constructor() {
        this.req = null;
        this.res = null;
        this.middlewares = null;
        this.router = null;
        this.staticDir = null;
        this.i = 0;
        this.next = this.next.bind(this);
    }

    init(req, res, middlewares, router, staticDir) {
        this.req = req;
        this.res = res;
        this.middlewares = middlewares;
        this.router = router;
        this.staticDir = staticDir;
        this.i = 0;
    }

    parseBody(callback) {
        if (this.req.method === 'GET' || this.req.method === 'HEAD') {
            this.req.body = {};
            return callback();
        }
        let raw = '';
        this.req.on('data', (chunk) => {
            raw += chunk;
        });
        this.req.on('end', () => {
            try {
                const ct = (this.req.headers['content-type'] || '').split(';')[0].trim();
                if (ct === 'application/x-www-form-urlencoded') {
                    const params = new URLSearchParams(raw);
                    const obj = {};
                    for (const [k, v] of params.entries()) obj[k] = v;
                    this.req.body = sanitizePayload(obj);
                } else {
                    this.req.body = raw ? sanitizePayload(JSON.parse(raw)) : {};
                }
            } catch {
                this.req.body = raw || {};
            }
            callback();
        });
    }

    serveStatic(callback) {
        if (!this.staticDir) return callback();

        let filePath = this.req.url === '/' ? '/index.html' : this.req.url;
        filePath = path.join(this.staticDir, filePath);

        if (!filePath.startsWith(this.staticDir)) {
            return callback();
        }

        fs.readFile(filePath, (err, content) => {
            if (err) return callback();
            const ext = path.extname(filePath);
            const contentType = MIME_TYPES[ext] || 'text/plain';
            this.res.writeHead(200, { 'Content-Type': contentType });
            this.res.end(content);
        });
    }

    handleError(err) {
        if (global.sentryCapture) global.sentryCapture(err);
        console.error('[fullstx] Error:', err);
        if (!this.res.headersSent) {
            this.res.writeHead(500, { 'Content-Type': 'application/json' });
            this.res.end(JSON.stringify({ error: 'Internal Server Error' }));
        }
    }

    next(err) {
        if (err) {
            return this.handleError(err);
        }

        if (this.i < this.middlewares.length) {
            try {
                const mw = this.middlewares[this.i++];
                const result = mw(this.req, this.res, this.next);
                if (result instanceof Promise) {
                    result.catch((e) => this.next(e));
                }
            } catch (e) {
                this.next(e);
            }
        } else {
            const route = this.router.lookup(this.req.method, this.req.pathname);
            if (route && route.handler) {
                this.res.route = route.routeConfig;
                this.req.params = Object.assign(this.req.params || {}, route.params);
                try {
                    const result = route.handler(this.req, this.res);
                    if (result instanceof Promise) {
                        result.catch((e) => this.handleError(e));
                    }
                } catch (e) {
                    this.handleError(e);
                }
            } else {
                this.serveStatic(() => {
                    if (!this.res.headersSent) {
                        this.res.writeHead(404);
                        this.res.end('404 Not Found');
                    }
                });
            }
        }
    }
}

const CONTEXT_POOL = [];
function getContext(req, res, middlewares, router, staticDir) {
    if (CONTEXT_POOL.length > 0) {
        const ctx = CONTEXT_POOL.pop();
        ctx.init(req, res, middlewares, router, staticDir);
        return ctx;
    }
    const ctx = new RequestContext();
    ctx.init(req, res, middlewares, router, staticDir);
    return ctx;
}
function releaseContext(ctx) {
    ctx.req = null;
    ctx.res = null;
    ctx.middlewares = null;
    ctx.router = null;
    if (CONTEXT_POOL.length < 1000) CONTEXT_POOL.push(ctx);
}

function createServer(router, middlewares, options = {}) {
    const staticDir = options.staticDir ? path.resolve(options.staticDir) : null;

    const server = http.createServer((rawReq, rawRes) => {
        const reqWrapper = getRequest(rawReq);
        const resWrapper = getResponse(rawRes, reqWrapper);
        const ctx = getContext(reqWrapper, resWrapper, middlewares, router, staticDir);

        let released = false;
        const releaseAll = () => {
            if (released) return;
            released = true;
            setImmediate(() => {
                releaseRequest(reqWrapper);
                releaseResponse(resWrapper);
                releaseContext(ctx);
            });
        };

        rawRes.on('finish', releaseAll);
        rawRes.on('close', releaseAll);

        ctx.parseBody(ctx.next);
    });

    return server;
}

module.exports = createServer;
